import { setEventTracking } from "@/utils";
import SetupList from "@components/business-components/organisms/SetupList";
import { Typography } from "@flash-tecnologia/hros-web-ui-v2";
import {
  useFlag,
  usePermissions,
  useSelectedCompany,
} from "@flash-tecnologia/hros-web-utility";
import { useGetSetup, useUpdateStepStatus } from "@hooks";
import { Step } from "bff/src/services";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { VIEW_SETUP_FEATURE_FLAG } from "src/shared/constants";
import { TrackingEvents } from "../SetupWorkflow/types";
import { Header, StyledPageContainer } from "./setup.styles";

export const SetupPage = () => {
  const isSetupEnabled = useFlag({ flagName: VIEW_SETUP_FEATURE_FLAG });
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { selectedCompany } = useSelectedCompany();
  const { setup, isLoading } = useGetSetup(selectedCompany.id);
  const { updateStepStatus } = useUpdateStepStatus(selectedCompany.id, {
    forceUpdateSetup: true,
  });

  useEffect(() => {
    if (!permissions.isAdmin) navigate("/home");
  }, [permissions]);

  const onStepClick = async (step: Step) => {
    if (step.status === "PENDING") {
      updateStepStatus({
        setupId: setup!.id,
        externalId: step.externalId,
        status: "DOING",
      });
    }
    setEventTracking(TrackingEvents.SETUP_STEP_ITEM_CLICKED, {
      step: step.externalId,
    });
    navigate(step.redirectPath);
  };

  const onSkipToggle = async (step: Step) => {
    const isSkipped = step.status === "SKIPPED";
    const newStatus = isSkipped ? "PENDING" : "SKIPPED";

    setEventTracking(
      isSkipped
        ? TrackingEvents.SETUP_STEP_ITEM_RECONSIDER_CLICKED
        : TrackingEvents.SETUP_STEP_ITEM_IGNORE_CLICKED,
      { step: step.externalId }
    );

    updateStepStatus({
      setupId: setup!.id,
      externalId: step.externalId,
      status: newStatus,
    });
  };

  if (isLoading) return null;

  return (
    <StyledPageContainer>
      <Header>
        <Typography variant="headline8" color="neutral.20">
          São poucos passos para usar a Flash!
        </Typography>
        <Typography variant="body4" color="neutral.40">
          Criamos o guia abaixo para te ajudar a iniciar sua jornada com a
          gente.
        </Typography>
      </Header>
      <SetupList.Root
        title="Comece preparando a plataforma"
        description="Adicione as informações da empresa e equipe em minutos."
      >
        {setup?.baseSteps.map((step, index) => (
          <SetupList.Item
            key={step.title}
            step={step}
            isLast={index === setup.baseSteps.length - 1}
            actionSlot={
              <SetupList.Actions
                step={step}
                onClick={() => onStepClick(step)}
                onSkipToggle={() => onSkipToggle(step)}
              />
            }
          />
        ))}
      </SetupList.Root>
    </StyledPageContainer>
  );
};
